// src/components/ScoreSubmissionModal.jsx - Updated for TSO integration
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';
import { submitScore } from '../services/LeaderboardService.ts';

const ScoreSubmissionModal = ({ 
  isOpen, 
  onClose, 
  gameStats
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  
  if (!isOpen) return null;
  
  const handleSubmitScore = async () => {
    try {
      // Check if user is logged in
      if (!user || !profile) {
        setError("You must be logged in to submit scores to the leaderboard");
        return;
      }
      
      setSubmitting(true);
      setError('');
      
      // Submit score using the service
      const result = await submitScore({
        userId: user.id,
        username: profile.username,
        score: gameStats.score,
        level: gameStats.level,
        classType: gameStats.classType
      });
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const goToLogin = () => {
    // For TSO integration, redirect to accounts system instead of local login
    const currentDomain = window.location.hostname;
    
    // Skip redirect for localhost development
    const isLocalhost = currentDomain === 'localhost' || 
                       currentDomain === '127.0.0.1' ||
                       currentDomain.includes('localhost');
    
    if (!isLocalhost) {
      const accountsUrl = `https://accounts.${currentDomain.replace(/^[^.]+\./, '')}/profile`;
      
      // Save the score data to localStorage before navigating
      localStorage.setItem('pendingGameScore', JSON.stringify(gameStats));
      onClose();
      
      window.location.href = accountsUrl;
    } else {
      // For localhost, just close the modal
      alert('SO account integration only available in production');
      onClose();
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 px-4">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-2xl font-bold mb-4 text-center text-yellow-400">Submit Your Score</h2>
        
        {error && (
          <div className="mb-4 p-2 bg-red-900/50 text-red-200 rounded text-sm">
            {error}
          </div>
        )}
        
        {!submitted ? (
          <>
            <div className="mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-700 p-4 rounded text-left">
                  <p className="text-gray-400 text-sm">Final Score</p>
                  <p className="text-yellow-300 text-2xl font-bold">{gameStats.score}</p>
                </div>
                <div className="bg-gray-700 p-4 rounded text-left">
                  <p className="text-gray-400 text-sm">Level Reached</p>
                  <p className="text-blue-300 text-2xl font-bold">{gameStats.level}</p>
                </div>
                <div className="bg-gray-700 p-4 rounded text-left">
                  <p className="text-gray-400 text-sm">Threats Eliminated</p>
                  <p className="text-green-300 text-xl font-bold">{gameStats.threatsDestroyed}</p>
                </div>
                <div className="bg-gray-700 p-4 rounded text-left">
                  <p className="text-gray-400 text-sm">Class</p>
                  <p className="text-purple-300 text-xl font-bold">{gameStats.classType}</p>
                </div>
              </div>
              
              {user && profile ? (
                <div className="mt-4 bg-gray-700 p-4 rounded">
                  <p className="text-gray-400 text-sm">Playing as</p>
                  <p className="text-green-300 text-xl">
                    {profile.username}
                  </p>
                </div>
              ) : (
                <div className="mt-4 p-4 bg-blue-900/30 rounded text-sm">
                  <h3 className="text-blue-300 font-bold mb-1">Login Required</h3>
                  <p className="text-blue-100 mb-3">
                    You need to be logged in with your Security Onion account to submit your score to the leaderboard. Create an account to track your scores and compete with other players!
                  </p>
                  <button
                    onClick={goToLogin}
                    className="w-full bg-blue-600 hover:bg-blue-700 py-2 rounded text-white"
                  >
                    Login with Security Onion Account
                  </button>
                </div>
              )}
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-gray-700 rounded hover:bg-gray-600"
              >
                Close
              </button>
              
              {user && profile && (
                <button
                  onClick={handleSubmitScore}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Score'}
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="text-green-400 text-4xl mb-4">✓</div>
              <p className="text-white text-lg mb-2">Score Submitted Successfully!</p>
              <p className="text-gray-400 text-sm">Your score has been added to the leaderboard.</p>
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-gray-700 rounded hover:bg-gray-600"
              >
                Close
              </button>
              
              <button
                onClick={() => {
                  onClose();
                  navigate('/leaderboard');
                }}
                className="flex-1 px-4 py-2 bg-blue-600 rounded hover:bg-blue-700"
              >
                View Leaderboard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ScoreSubmissionModal;